import { useEffect, useState } from 'react'
import { Download, FileSpreadsheet, Sparkles, Upload } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'
import {
    autoMap,
    getMapping,
    ingestReadings,
    saveMapping,
} from '~/api/monitoring'
import type { MappingProposal, MonitoringIndicator } from '~/api/types'
import { errMessage } from '~/lib/errors'
import { Dropzone, parseSpreadsheet, type Records } from './ingest-helpers'
import { MappingTable } from './mapping-table'

const SOURCE_TYPE = 'excel' // Phase 1: CSV 업로드. API/DB는 준비 중.

/** CSV·엑셀 업로드 → 컬럼을 정량지표에 매핑하고 측정값을 적재한다. */
export function CsvIngestPanel({
    dashboardId,
    indicators,
    onIngested,
}: {
    dashboardId: string
    indicators: MonitoringIndicator[]
    onIngested?: () => void
}) {
    const [columns, setColumns] = useState<string[]>([])
    const [records, setRecords] = useState<Records>([])
    const [filename, setFilename] = useState<string | null>(null)
    const [mapping, setMapping] = useState<Record<string, string>>({})
    const [proposals, setProposals] = useState<Record<string, MappingProposal>>(
        {}
    )
    const [measuredAtKey, setMeasuredAtKey] = useState<string>('')
    const [aiBusy, setAiBusy] = useState(false)
    const [busy, setBusy] = useState(false)
    const [msg, setMsg] = useState<string | null>(null)
    const [err, setErr] = useState<string | null>(null)

    useEffect(() => {
        getMapping(dashboardId, SOURCE_TYPE)
            .then((m) => setMapping(m.mapping ?? {}))
            .catch(() => setMapping({}))
    }, [dashboardId])

    const onFile = async (file: File) => {
        setErr(null)
        setMsg(null)
        try {
            const parsed = await parseSpreadsheet(file)
            if (parsed.columns.length === 0) {
                setErr(
                    '파일에서 컬럼을 읽지 못했어요. 첫 행에 컬럼명이 있는지 확인해 주세요.'
                )
                return
            }
            setColumns(parsed.columns)
            setRecords(parsed.records)
            setFilename(file.name)
            setProposals({})
            // 시각 컬럼 자동 추정(이름에 일시/시각/date/time 포함).
            const tk = parsed.columns.find((c) =>
                /일시|시각|date|time/i.test(c)
            )
            if (tk) setMeasuredAtKey(tk)
        } catch {
            setErr(
                '파일을 읽지 못했어요. CSV 또는 Excel(.xlsx) 형식인지 확인해 주세요.'
            )
        }
    }

    const runAutoMap = async () => {
        if (records.length === 0) return
        setAiBusy(true)
        setErr(null)
        try {
            const res = await autoMap(dashboardId, {
                sourceType: SOURCE_TYPE,
                sample: records.slice(0, 20),
            })
            const next = { ...mapping }
            const props: Record<string, MappingProposal> = {}
            for (const p of res.proposals) {
                props[p.sourceKey] = p
                if (p.fieldId) next[p.sourceKey] = p.fieldId
            }
            setMapping(next)
            setProposals(props)
            setMsg('AI가 매핑을 제안했어요. 확인 후 저장하세요.')
        } catch (e: unknown) {
            setErr(errMessage(e, 'AI 매핑에 실패했어요.'))
        } finally {
            setAiBusy(false)
        }
    }

    const setColMap = (col: string, fieldId: string) => {
        setMapping((m) => {
            const next = { ...m }
            if (fieldId) next[col] = fieldId
            else delete next[col]
            return next
        })
    }

    const onSave = async () => {
        setBusy(true)
        setErr(null)
        try {
            await saveMapping(dashboardId, { sourceType: SOURCE_TYPE, mapping })
            setMsg('매핑을 저장했어요.')
        } catch (e: unknown) {
            setErr(errMessage(e, '저장에 실패했어요.'))
        } finally {
            setBusy(false)
        }
    }

    const onIngest = async () => {
        if (records.length === 0) return
        setBusy(true)
        setErr(null)
        try {
            await saveMapping(dashboardId, { sourceType: SOURCE_TYPE, mapping })
            const res = await ingestReadings(dashboardId, {
                sourceType: SOURCE_TYPE,
                records,
                measuredAtKey: measuredAtKey || null,
                source: 'upload',
                filename,
            })
            setMsg(
                `측정값 ${res.inserted}건을 적재했어요. '데이터베이스' 탭에서 확인하세요.`
            )
            onIngested?.()
        } catch (e: unknown) {
            setErr(errMessage(e, '적재에 실패했어요.'))
        } finally {
            setBusy(false)
        }
    }

    // 선택한 평가의 정량지표로 CSV 양식을 동적 생성해 다운로드(하드코딩 아님 — 카탈로그서 도출).
    const downloadTemplate = () => {
        const esc = (s: string) =>
            /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        const cols = [
            '측정일시',
            ...indicators.map(
                (i) => `${i.nameKo}${i.unit ? ` (${i.unit})` : ''}`
            ),
        ]
        const header = cols.map(esc).join(',')
        const example = ['2026-06-25 13:40', ...indicators.map(() => '')]
            .map(esc)
            .join(',')
        // Excel 한글 깨짐 방지 BOM(U+FEFF) + 헤더 + 예시 행 1개.
        const csv = `\uFEFF${header}\n${example}\n`
        const url = URL.createObjectURL(
            new Blob([csv], { type: 'text/csv;charset=utf-8' })
        )
        const a = document.createElement('a')
        a.href = url
        a.download = 'CCP_모니터링_양식.csv'
        a.click()
        URL.revokeObjectURL(url)
    }

    return (
        <>
            <section className='border-border bg-card flex flex-col rounded-md border p-5'>
                <div className='mb-3 flex items-start gap-3'>
                    <div className='bg-accent text-primary grid size-10 shrink-0 place-items-center rounded-md'>
                        <FileSpreadsheet className='size-5' />
                    </div>
                    <div>
                        <h3 className='text-foreground text-sm font-semibold'>
                            CSV·엑셀 업로드
                        </h3>
                        <p className='text-muted-foreground mt-0.5 text-xs'>
                            표 형태 일지를 올려 컬럼을 지표에 매핑합니다.
                        </p>
                    </div>
                </div>
                <Dropzone
                    accept='.csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    onFile={onFile}
                    busy={false}
                    icon={<Upload className='size-5' />}
                    title='CSV·엑셀 파일 끌어다 놓기 또는 클릭'
                    hint='.csv / .xlsx · 첫 행은 컬럼명'
                />
                <div className='border-border mt-3 flex items-center justify-between gap-2 border-t pt-3'>
                    <span className='text-muted-foreground text-xs'>
                        처음이세요? 양식을 받아 채우면 쉬워요.
                    </span>
                    <Button
                        variant='outline'
                        size='sm'
                        onClick={downloadTemplate}
                        disabled={indicators.length === 0}>
                        <Download />
                        양식 받기
                    </Button>
                </div>
                {columns.length > 0 ? (
                    <p className='text-primary mt-2 text-xs'>
                        ✓ {columns.length}개 컬럼 · {records.length}행 읽음 —
                        아래에서 매핑하세요
                    </p>
                ) : null}
            </section>

            {columns.length > 0 ? (
                <section className='border-border bg-card rounded-md border p-5'>
                    <div className='mb-3 flex items-center justify-between gap-3'>
                        <div>
                            <h2 className='text-foreground text-sm font-semibold'>
                                컬럼 → 지표 매핑
                            </h2>
                            <p className='text-muted-foreground mt-0.5 text-xs'>
                                직접 고르거나, AI 제안을 받아 확인 후
                                저장하세요.
                            </p>
                        </div>
                        <Button
                            variant='outline'
                            onClick={runAutoMap}
                            loading={aiBusy}>
                            {aiBusy ? null : <Sparkles />}
                            AI가 매핑 판단하기
                        </Button>
                    </div>

                    <MappingTable
                        keys={columns.map((col) => ({
                            key: col,
                            sampleText: records
                                .map((r) => r[col])
                                .filter(Boolean)
                                .slice(0, 5)
                                .join(', '),
                        }))}
                        mapping={mapping}
                        proposals={proposals}
                        onMap={setColMap}
                        indicators={indicators}
                        keyHeader='소스 컬럼'
                        measuredAtKey={measuredAtKey}
                        onMeasuredAtKey={setMeasuredAtKey}
                        measuredAtLabel='측정 시각 컬럼'
                        measuredAtOptions={[
                            {
                                value: '',
                                label: '(없음 — 적재 시각 사용)',
                            },
                            ...columns.map((c) => ({ value: c, label: c })),
                        ]}
                    />

                    <div className='mt-4 flex items-center gap-2'>
                        <Button onClick={onIngest} loading={busy}>
                            매핑 저장 + 측정값 적재
                        </Button>
                        <Button
                            variant='outline'
                            onClick={onSave}
                            disabled={busy}>
                            매핑만 저장
                        </Button>
                    </div>
                </section>
            ) : null}

            {msg ? (
                <ErrorBanner tone='success' className='bg-accent text-primary'>
                    {msg}
                </ErrorBanner>
            ) : null}
            {err ? (
                <ErrorBanner className='bg-point-subtle text-point'>
                    {err}
                </ErrorBanner>
            ) : null}
        </>
    )
}
